import { Component, computed, effect, inject, OnDestroy, OnInit, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { EnrollmentStatus } from '@lb/api/mylb/enums/enrollment-status.enum';
import { NoDataMessageComponent } from '@lb/components/no-data-message/no-data-message.component';
import { PageHeaderComponent } from '@lb/components/page-header/page-header.component';
import { TwoColumnPageLayoutComponent } from '@lb/components/page-templates/two-column-page-layout/two-column-page-layout.component';
import { MDIconComponent } from '@melodic-ui/components';
import { ComponentLoaderService } from '@melodic-ui/services';
import { combineLatest, filter } from 'rxjs';
import { EnrollmentInfoPanelComponent } from '../../shared/components/enrollment-info-panel/enrollment-info-panel.component';
import { ReadonlyLessonCardComponent } from '@lb/components/lessons/readonly-lesson-card/readonly-lesson-card.component';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { NotificationsComponent } from '@lb/components/notifications/notifications.component';
import { NeedHelpCardComponent } from '@lb/components/need-help-card/need-help-card.component';
import { LearnerUserStateService } from '../../shared/state/learner-users-state.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { LessonV2Service } from '@lb/api/mylb/services';
import { EnrollmentDetailService } from '@lb/api/mylb/services/enrollment-detail.service';
import { ILessonListItem, IEnrollmentDetailItem } from '@lb/api/mylb/interfaces';
import { LessonStateService } from '@lb/state/lesson-state.service';
import { EnrollmentCardComponent } from '@lb/components/enrollment-card/enrollment-card.component';
import { LearnerEnrollmentCardComponent } from '../enrollments/learner-enrollment-card.component';
import { LearnerLessonCardComponent } from '../enrollments/learner-lesson-card.component';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { ILessonDetailsSource, usesLessonDetailsV2 } from '@lb/functions';

@Component({
	selector: 'lrnr-home',
	standalone: true,
	imports: [
		RouterModule,
		TwoColumnPageLayoutComponent,
		ReadonlyLessonCardComponent,
		NoDataMessageComponent,
		EnrollmentInfoPanelComponent,
		EnrollmentCardComponent,
		LearnerEnrollmentCardComponent,
		LearnerLessonCardComponent,
		NotificationsComponent,
		NeedHelpCardComponent,
		LoaderComponent,
		MDIconComponent
	],
	templateUrl: './home.component.html',
	styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
	private _componentLoaderService: ComponentLoaderService = inject(ComponentLoaderService);
	private _lessonV2Service = inject(LessonV2Service);
	private _enrollmentDetailService = inject(EnrollmentDetailService);
	private _lessonStateService = inject(LessonStateService);
	private _learnerUserStateService = inject(LearnerUserStateService);
	private _activeLearnerUserID: Signal<number | undefined> = this._learnerUserStateService.activeLearnerUserID;
	private _accountStateService = inject(AccountStateService);
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);

	protected readonly usesV2 = (item: ILessonDetailsSource) => usesLessonDetailsV2(item, this._appSettings);

	public firstName = computed<string>(() => this._accountStateService.user().preferredName || this._accountStateService.user().firstName);

	public isEnrollmentsLoading = signal<boolean>(true);

	public lessons = signal<ILessonListItem[]>([]);

	// The page is sourced entirely from Enrollment.Api detail items — selection, pager, info panel and
	// cards all read the same list, so chirp room memberships (D19) appear here like any enrollment.
	public enrollmentDetails = signal<IEnrollmentDetailItem[]>([]);
	private _currentEnrollmentID = signal<number | null>(null);

	public activeEnrollments = computed(() => this.enrollmentDetails().filter((detail) => detail.isActive));

	public enrollmentDetail = computed(() => {
		const active = this.activeEnrollments();
		const currentID = this._currentEnrollmentID();
		return active.find((detail) => detail.enrollmentID === currentID) ?? (active.length > 0 ? active[0] : undefined);
	});

	// The schedule call-to-action is only for the enrollment's primary learner. A chirp room member
	// never sets the room's schedule, so a member-visible enrollment in AwaitingSchedule renders the
	// normal card instead. Guardians inherit the right behavior: the comparison is against the active
	// learner being viewed, not the logged-in user.
	public isAwaitingSchedule = computed(() => {
		const detail = this.enrollmentDetail();
		return detail?.enrollmentStatusID === EnrollmentStatus.AwaitingSchedule && detail.learnerUserID === this._activeLearnerUserID();
	});

	public isFirstEnrollment = computed(() => {
		const active = this.activeEnrollments();
		if (active.length === 0) return false;
		return active[0]?.enrollmentID === this.enrollmentDetail()?.enrollmentID;
	});

	public isLastEnrollment = computed(() => {
		const active = this.activeEnrollments();
		if (active.length === 0) return false;
		return active[active.length - 1]?.enrollmentID === this.enrollmentDetail()?.enrollmentID;
	});

	constructor() {
		effect(() => {
			const firstName = this.firstName();
			if (firstName) {
				this._componentLoaderService.getContainer('banner-main-content').inputs.set({
					pageTitle: `Hello, ${firstName}!`
				});
			}
		});

		// Load enrollments and lessons when active learner changes
		// Query param handling is done at app.component level
		combineLatest([toObservable(this._activeLearnerUserID), toObservable(this._lessonStateService.lessonChanged)])
			.pipe(
				filter(([userID, _]) => userID !== undefined),
				takeUntilDestroyed()
			)
			.subscribe({
				next: ([activeLearnerUserID, _]) => {
					if (!activeLearnerUserID) {
						return;
					}

					this.loadEnrollmentDetails(activeLearnerUserID);
					this.loadLessons(activeLearnerUserID);
				},
				error: (error) => {
					console.error('Error loading enrollments', error);
				}
			});
	}

	ngOnInit(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
		this._componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: `Hello, ${this.firstName()}!` });
	}

	loadEnrollmentDetails(activeLearnerUserID: number) {
		this.isEnrollmentsLoading.set(true);
		this._enrollmentDetailService.getEnrollmentDetails({ learnerId: activeLearnerUserID }).subscribe({
			next: (details) => {
				this.enrollmentDetails.set(details);
				this.isEnrollmentsLoading.set(false);
			},
			error: (error) => {
				console.error('Error loading enrollment details', error);
				this.isEnrollmentsLoading.set(false);
			}
		});
	}

	loadLessons(activeLearnerUserID: number) {
		this._lessonV2Service.getUpcoming(activeLearnerUserID, 'learner', 1, 3).subscribe({
			next: (response) => {
				this.lessons.set(response.items);
			},
			error: (error) => {
				console.error('Error loading lessons', error);
			}
		});
	}

	ngOnDestroy(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(null);
	}

	goToNextEnrollment() {
		const active = this.activeEnrollments();
		const currentIndex = active.findIndex((e) => e.enrollmentID === this.enrollmentDetail()?.enrollmentID);
		if (currentIndex < active.length - 1) {
			this._currentEnrollmentID.set(active[currentIndex + 1].enrollmentID);
		}
	}

	goToPreviousEnrollment() {
		const active = this.activeEnrollments();
		const currentIndex = active.findIndex((e) => e.enrollmentID === this.enrollmentDetail()?.enrollmentID);
		if (currentIndex > 0) {
			this._currentEnrollmentID.set(active[currentIndex - 1].enrollmentID);
		}
	}
}
